import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Filter } from 'lucide-react';
import { fetchBlogs } from '../lib/firebaseBlog';
import { BlogCard } from '../components/BlogCard';

const CATEGORIES = [
  { value: 'all', label: 'All Posts' },
  { value: 'tech', label: 'Syntax & Systems' },
  { value: 'rabbit_hole', label: 'Rabbit Hole' },
];

export function BlogIndexPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const load = async () => {
      const data = await fetchBlogs();
      setPosts(data);
      setLoading(false);
    };
    load();
  }, []);

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col pt-16">
      <main className="flex-1 pb-24 px-4 md:px-6 pt-10">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs font-bold uppercase tracking-[0.3em] text-temple-red font-body"
              >
                Thoughts & Explorations
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="font-heading text-4xl md:text-5xl font-bold text-[#8B4513] mt-2"
              >
                The <span className="italic">Rabbit Hole Effect</span>
              </motion.h1>
            </div>

            {/* Category Filter */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4 text-muted-foreground" />
              <div className="flex gap-2">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`px-4 py-2 font-body text-sm rounded-full transition-all ${
                      selectedCategory === cat.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid={`filter-${cat.value}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Posts Grid */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="blog-cards">
            {loading && (
              <div className="col-span-full flex justify-center py-20" data-testid="blog-loading">
                <div
                  className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin"
                  aria-hidden
                />
              </div>
            )}

            {!loading && filteredPosts.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full text-center py-20"
                data-testid="blog-empty"
              >
                <div className="max-w-md mx-auto">
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-3">
                    {selectedCategory === 'all' 
                      ? 'No posts yet' 
                      : `No posts in ${CATEGORIES.find(c => c.value === selectedCategory)?.label}`}
                  </h2>
                  <p className="font-body text-muted-foreground mb-6">
                    {selectedCategory === 'all'
                      ? 'Check back later for new content.'
                      : 'Try selecting a different category or check back later.'}
                  </p>
                  {selectedCategory !== 'all' && (
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-body text-sm rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      View All Posts
                    </button>
                  )}
                  <Link to="/" className="block mt-4 text-primary hover:underline text-sm font-body">
                    Back to home
                  </Link>
                </div>
              </motion.div>
            )}

            {!loading && filteredPosts.length > 0 && filteredPosts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <BlogCard post={post} />
              </motion.div>
            ))}
          </section>

          {/* Back to Home */}
          {!loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-16 text-center"
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Home
              </Link>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
